import * as React from 'react';
import {Suspense} from 'react';
import {graphql, useLazyLoadQuery} from 'react-relay';
import {Link, useNavigate, useSearchParams} from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {FacebookLoginForm} from '../components/AuthMenu';
import EngagementBuilder from '../engagement/EngagementBuilder';
import {INTERESTS} from '../engagement/draft';
import {ClientEngagementNewPageQuery} from './__generated__/ClientEngagementNewPageQuery.graphql';

const QUERY = graphql`
    query ClientEngagementNewPageQuery {
        viewer { id name }
        facebookLoginEnabled
    }`;

// Customer-portal host: the signed-in customer builds an engagement for themselves,
// so there's no contact step.
const CustomerBuilder: React.FC = () => {
    const data = useLazyLoadQuery<ClientEngagementNewPageQuery>(QUERY, {}, {fetchPolicy: 'store-and-network'});
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const preselected = (params.get('interest') ?? '').split(',').filter((k) => INTERESTS.some((i) => i.key === k));

    if (!data.viewer) {
        return (
            <div className="max-w-md mx-auto text-center border border-gray-800 bg-[#050505] p-10">
                <p className="text-gray-400 text-sm mb-6">Sign in to start an engagement under your account, or <Link to="/start" className="text-lumina-accent">start one without signing in</Link>.</p>
                {data.facebookLoginEnabled && (
                    <FacebookLoginForm className="w-full py-3 bg-[#1877F2] text-white font-bold text-sm tracking-widest uppercase hover:bg-[#1877F2]/80">
                        Continue with Facebook
                    </FacebookLoginForm>
                )}
            </div>
        );
    }

    return (
        <EngagementBuilder
            host="customer"
            initialDraft={{interests: preselected}}
            signedInAs={data.viewer.name}
            onSubmitted={(id) => navigate(`/client/engagements/${encodeURIComponent(id)}?submitted=1`)}
        />
    );
};

const ClientEngagementNewPage: React.FC = () => (
    <div className="bg-lumina-dark min-h-screen text-white">
        <Navbar/>
        <main className="max-w-7xl mx-auto px-6 pt-28 pb-24">
            <div className="text-center mb-10">
                <span className="text-lumina-purple font-display text-sm tracking-wider uppercase">New engagement</span>
                <h1 className="text-4xl md:text-5xl font-display font-bold mt-2">WHAT'S <span className="text-gray-600">NEXT?</span></h1>
            </div>
            <Suspense fallback={<p className="text-gray-500 text-sm text-center">Loading…</p>}>
                <CustomerBuilder/>
            </Suspense>
        </main>
        <Footer/>
    </div>
);

export default ClientEngagementNewPage;
